import { REPORT_CATEGORY_OPTIONS, REPORTS } from '@/app/dashboards/utils/reports.enum';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import toNumber from 'lodash/toNumber';

export const AGGREGATE_REPORT_TYPES = Object.freeze({
  BRAND: {
    value: 'BRAND',
    text: 'by Brand',
    label: 'by Brand',
    icon: 'user',
    formattedValue: 'Brand',
  },
  CHANNEL: {
    value: 'CHANNEL',
    text: 'by Channel',
    label: 'by Channel',
    icon: 'feed',
    formattedValue: 'Channel',
  },
  TAG: {
    value: 'TAG',
    text: 'by Tag',
    label: 'by Tag',
    icon: 'people-user',
    formattedValue: 'Tag',
  },
});

export const BASE_BREAKDOWN_OPTIONS = Object.freeze({
  NONE: {
    label: 'None',
    value: 'NONE',
    hideByVisualizationTypes: ['LINE_CHART', 'GRAPH', 'DOUGHNUT_CHART'],
  },
  AGE: {
    label: 'Age',
    value: 'AGE',
  },
  ASSIGNEE: {
    label: 'Assignee',
    value: 'ASSIGNEE',
  },
  BRAND: {
    label: 'Brand',
    value: 'BRAND',
  },
  CHANNEL: {
    label: 'Channel',
    value: 'CHANNEL',
  },
  CITY: {
    label: 'City',
    value: 'CITY',
  },
  COUNTRY: {
    label: 'Country',
    value: 'COUNTRY',
  },
  GENDER: {
    label: 'Gender',
    value: 'GENDER',
  },
  INDUSTRY: {
    label: 'Industry',
    value: 'INDUSTRY',
  },
  MEDIA_TYPE: {
    label: 'Media Type',
    value: 'MEDIA_TYPE',
  },
  MENTION_TYPE: {
    label: 'Mention Type',
    value: 'MENTION_TYPE',
  },
  MESSAGE_TYPE: {
    label: 'Message Type',
    value: 'MESSAGE_TYPE',
  },
  SENTIMENT: {
    label: 'Sentiment',
    value: 'SENTIMENT',
  },
  STATE_PROVINCE: {
    label: 'State/Province',
    value: 'STATE_PROVINCE',
  },
  TOPIC: {
    label: 'Topic',
    value: 'TOPIC',
  },
});

export const CREATOR_AGGREGATE_TYPES = Object.freeze({
  BRAND: {
    value: 'BRAND',
    text: 'by Creators',
    label: 'by Creators',
    icon: 'user',
    formattedValue: 'Creators',
  },
});

export const METRIC_DISPLAY_OPTIONS = Object.freeze({
  TOTAL: {
    value: 'TOTAL',
    key: 'TOTAL',
    text: 'Aggregate Total',
    label: 'Aggregate Total',
    icon: 'lineItems',
  },
  TABLE: {
    value: 'TABLE',
    key: 'TABLE',
    text: 'Table',
    label: 'Table',
    icon: 'table',
  },
});

export const ADS_METRIC_DISPLAY_OPTIONS = Object.freeze({
  ADS_TOTAL_METRIC: {
    value: REPORTS.ADS_TOTAL_METRIC.value,
    text: 'Aggregate Total',
    label: 'Aggregate Total',
    icon: 'user',
  },
  ADS_TOTAL_GROUPED_METRIC: {
    value: REPORTS.ADS_TOTAL_GROUPED_METRIC.value,
    text: 'Table',
    label: 'Table',
    icon: 'table',
  },
});

// Base shared aggregate types used across multiple report categories
export const SHARED_REPORT_AGGREGATE_BY_TYPES = Object.freeze({
  BRAND: {
    value: 'BRAND',
    label: 'by Brand',
    formattedValue: 'Brand',
  },
  CHANNEL: {
    value: 'CHANNEL',
    label: 'by Channel',
    formattedValue: 'Channel',
  },
  COUNTRY: {
    value: 'COUNTRY',
    text: 'by Country',
    label: 'by Country',
    formattedValue: 'Country',
    genericLabels: ['Country 1', 'Country 2', 'Country 3'],
  },
  SENTIMENT: {
    value: 'SENTIMENT',
    label: 'by Sentiment',
    formattedValue: 'Sentiment',
  },
  TAG: {
    value: 'TAG',
    label: 'by Tag',
    formattedValue: 'Tag',
  },
});

export const ADS_REPORT_AGGREGATE_BY_TYPES = Object.freeze({
  BRAND: {
    value: 'BRAND',
    text: 'by Brand',
    label: 'by Brand',
    formattedValue: 'Brand',
  },
  TOTAL: {
    value: 'TOTAL',
    text: 'Aggregate Total',
    label: 'Aggregate Total',
  },
  SOURCE_CAMPAIGN: {
    value: 'SOURCE_CAMPAIGN',
    text: 'by Campaign',
    label: 'by Campaign',
    formattedValue: 'Campaign',
    genericLabels: ['Campaign 1', 'Campaign 2', 'Campaign 3'],
  },
  PUBLISHER_PLATFORM: {
    value: 'PUBLISHER_PLATFORM',
    text: 'by Channel',
    label: 'by Channel',
    formattedValue: 'Channel',
    genericLabels: ['Facebook', 'Instagram', 'Messenger', 'Meta Audience Network'],
  },
  AGE_GENDER: {
    value: 'AGE_GENDER',
    text: 'by Age and Gender',
    label: 'by Age and Gender',
    formattedValue: 'Age and Gender',
    genericLabels: ['Men', 'Women', 'Undefined'],
  },
  COUNTRY: {
    ...SHARED_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
  },
  REGION: {
    value: 'REGION',
    text: 'by Region',
    formattedValue: 'Region',
    label: 'by Region',
    genericLabels: ['Region 1', 'Region 2', 'Region 3'],
  },
});

export const COMMUNITY_REPORT_AGGREGATE_BY_TYPES = Object.freeze({
  ASSIGNEE: {
    value: 'ASSIGNEE',
    label: 'by Assignee',
    formattedValue: 'Assignee',
    filterKey: 'assignee_ids',
    keyIdGetter: (key) => toNumber(key.replace('ASSIGNEE_', '')),
  },
  BRAND: {
    value: 'BRAND',
    label: 'by Brand',
    formattedValue: 'Brand',
  },
  CHANNEL: {
    ...SHARED_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  },
  MESSAGE_TYPE: {
    value: 'MESSAGE_TYPE',
    label: 'by Message Type',
    formattedValue: 'Message Type',
  },
  MESSAGE_TAG: {
    value: 'MESSAGE_TAG',
    label: 'by Message Tag',
    formattedValue: 'Message Tag',
    filterKey: 'message_tag_ids',
    keyIdGetter: (key) => toNumber(key.replace('MESSAGE_TAG_', '')),
  },
  SENTIMENT: {
    ...SHARED_REPORT_AGGREGATE_BY_TYPES.SENTIMENT,
  },
});

export const LISTENING_REPORT_AGGREGATE_BY_TYPES = Object.freeze({
  TOTAL: {
    value: 'TOTAL',
    label: 'by Total',
    formattedValue: 'Total',
    fullLabel: 'Aggregate (Single Line)',
    hideByVisualizationTypes: [
      'VERTICAL_BAR_CHART',
      'HORIZONTAL_BAR_CHART',
      'DOUGHNUT_CHART',
      'TABLE',
    ],
  },
  CHANNEL: {
    ...SHARED_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  },
  COUNTRY: {
    ...SHARED_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
  },
  SENTIMENT: {
    ...SHARED_REPORT_AGGREGATE_BY_TYPES.SENTIMENT,
    allowedBreakdownOptions: [BASE_BREAKDOWN_OPTIONS.NONE, BASE_BREAKDOWN_OPTIONS.TOPIC],
  },
  TOPIC: {
    value: 'TOPIC',
    label: 'by Topic',
    formattedValue: 'Topic',
    keyIdGetter: (key) => toNumber(key.replace('TOPIC_', '')),
  },
  MEDIA_TYPE: {
    value: 'MEDIA_TYPE',
    label: 'by Media Type',
    formattedValue: 'Media Type',
    allowedBreakdownOptions: [
      BASE_BREAKDOWN_OPTIONS.NONE,
      BASE_BREAKDOWN_OPTIONS.SENTIMENT,
      BASE_BREAKDOWN_OPTIONS.TOPIC,
    ],
  },
  STATE_PROVINCE: {
    value: 'STATE_PROVINCE',
    label: 'by State/Province',
    formattedValue: 'State/Province',
    allowedBreakdownOptions: [
      BASE_BREAKDOWN_OPTIONS.NONE,
      BASE_BREAKDOWN_OPTIONS.SENTIMENT,
      BASE_BREAKDOWN_OPTIONS.TOPIC,
    ],
  },
  CITY: {
    value: 'CITY',
    label: 'by City',
    formattedValue: 'City',
    allowedBreakdownOptions: [
      BASE_BREAKDOWN_OPTIONS.NONE,
      BASE_BREAKDOWN_OPTIONS.SENTIMENT,
      BASE_BREAKDOWN_OPTIONS.TOPIC,
    ],
  },
  MENTION_TYPE: {
    value: 'MENTION_TYPE',
    label: 'by Mention Type',
    formattedValue: 'Mention Type',
    allowedBreakdownOptions: [
      BASE_BREAKDOWN_OPTIONS.NONE,
      BASE_BREAKDOWN_OPTIONS.SENTIMENT,
      BASE_BREAKDOWN_OPTIONS.TOPIC,
    ],
  },
  INDUSTRY: {
    value: 'INDUSTRY',
    label: 'by Industry',
    formattedValue: 'Industry',
    keyIdGetter: (key) => toNumber(key.replace('INDUSTRY_', '')),
    allowedBreakdownOptions: [
      BASE_BREAKDOWN_OPTIONS.NONE,
      BASE_BREAKDOWN_OPTIONS.SENTIMENT,
      BASE_BREAKDOWN_OPTIONS.TOPIC,
    ],
  },
  AGE: {
    value: 'AGE',
    label: 'by Age',
    formattedValue: 'Age',
    genericLabels: ['18-24', '25-34', '35-44', '45-54', '55-64', '65+'],
    allowedBreakdownOptions: [
      BASE_BREAKDOWN_OPTIONS.NONE,
      BASE_BREAKDOWN_OPTIONS.SENTIMENT,
      BASE_BREAKDOWN_OPTIONS.TOPIC,
    ],
    hideByVisualizationTypes: ['LINE_CHART', 'GRAPH'],
  },
  GENDER: {
    value: 'GENDER',
    label: 'by Gender',
    formattedValue: 'Gender',
    genericLabels: ['Men', 'Women', 'Undefined'],
    allowedBreakdownOptions: [
      BASE_BREAKDOWN_OPTIONS.NONE,
      BASE_BREAKDOWN_OPTIONS.SENTIMENT,
      BASE_BREAKDOWN_OPTIONS.TOPIC,
    ],
    hideByVisualizationTypes: ['LINE_CHART', 'GRAPH'],
  },
});

export const ADS_CHANNEL_TO_AGGREGATE_BY_TYPES = Object.freeze({
  [CHANNELS.META_ADS.value]: [
    ADS_REPORT_AGGREGATE_BY_TYPES.BRAND,
    ADS_REPORT_AGGREGATE_BY_TYPES.SOURCE_CAMPAIGN,
    ADS_REPORT_AGGREGATE_BY_TYPES.PUBLISHER_PLATFORM,
    ADS_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
    ADS_REPORT_AGGREGATE_BY_TYPES.REGION,
    ADS_REPORT_AGGREGATE_BY_TYPES.AGE_GENDER,
  ],
  [CHANNELS.TIKTOK_ADS.value]: [
    ADS_REPORT_AGGREGATE_BY_TYPES.BRAND,
    ADS_REPORT_AGGREGATE_BY_TYPES.SOURCE_CAMPAIGN,
    ADS_REPORT_AGGREGATE_BY_TYPES.AGE_GENDER,
    ADS_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
  ],
});

// TODO: remove legacy constants gdiDashboards https://app.shortcut.com/dashhudson/story/155883/remove-gdi-dashboard-feature-flag
export const GRAPH_DISPLAY_OPTIONS = Object.freeze({
  LINE_CHART: {
    value: 'GRAPH',
    text: 'Line Chart',
    label: 'Line Chart',
    key: 'LINE_CHART',
    icon: 'lineItems',
    disabledTooltip: 'Multi-brand multi-channel reports are not supported',
  },
  SINGLE_BAR_GRAPH: {
    value: 'SINGLE_BAR_GRAPH',
    text: 'Single Bar Graph',
    label: 'Single Bar Graph',
    key: 'SINGLE_BAR_GRAPH',
    icon: 'singleUser',
    disabledTooltip: 'Bar charts are not available when multiple media types are selected',
  },
  STACKED_BAR_GRAPH: {
    value: 'STACKED_BAR_GRAPH',
    text: 'Stacked Bar Graph',
    label: 'Stacked Bar Graph',
    key: 'STACKED_BAR_GRAPH',
    icon: 'stackedGraph',
    disabledTooltip: 'Bar charts are not available when multiple media types are selected',
  },
});

// New Graph Display Options for Menu
export const GRAPH_DISPLAY_OPTIONS_FOR_MENU = Object.freeze({
  LINE_CHART: {
    value: 'GRAPH',
    text: 'Line Chart',
    label: 'Line Chart',
    key: 'LINE_CHART',
    icon: 'line-chart-up',
    disabledTooltip: 'Multi-brand multi-channel reports are not supported',
  },
  VERTICAL_BAR_CHART: {
    value: 'VERTICAL_BAR_CHART',
    text: 'Vertical Bar Chart',
    label: 'Vertical Bar Chart',
    key: 'VERTICAL_BAR_CHART',
    icon: 'bar-chart',
    tooltip:
      'Displays metric values as vertical bars across categories such as brands or channels. \n\nEach bar’s height represents the value of the selected metric.',
  },
  HORIZONTAL_BAR_CHART: {
    value: 'HORIZONTAL_BAR_CHART',
    text: 'Horizontal Bar Chart',
    label: 'Horizontal Bar Chart',
    key: 'HORIZONTAL_BAR_CHART',
    icon: 'horizontal-bar-chart',
    tooltip:
      'Displays metric values as horizontal bars across categories such as brands or channels. \n\nEach bar’s length represents the value of the selected metric.',
  },
  DOUGHNUT_CHART: {
    value: 'DOUGHNUT_CHART',
    text: 'Donut Chart',
    label: 'Donut Chart',
    key: 'DOUGHNUT_CHART',
    icon: 'pie-chart',
    disabledTooltip: 'Multi-brand multi-channel reports are not supported',
  },
});

// REPORT TYPE CONFIGS

export const REPORT_PREVIEW_AGGREGATE_BY_TYPES = Object.freeze({
  ...SHARED_REPORT_AGGREGATE_BY_TYPES,
  ...ADS_REPORT_AGGREGATE_BY_TYPES,
  ...COMMUNITY_REPORT_AGGREGATE_BY_TYPES,
  ...LISTENING_REPORT_AGGREGATE_BY_TYPES,
});

export const BASE_AGGREGATE_TO_REPORT_TYPE = [
  [METRIC_DISPLAY_OPTIONS.TOTAL.value, REPORTS.TOTAL_METRIC.value],
  [METRIC_DISPLAY_OPTIONS.TABLE.value, REPORTS.TOTAL_GROUPED_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.LINE_CHART.value, REPORTS.GRAPH.value],
  [GRAPH_DISPLAY_OPTIONS.SINGLE_BAR_GRAPH.value, REPORTS.BAR_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.STACKED_BAR_GRAPH.value, REPORTS.STACKED_BAR_METRIC.value],
];

export const OWNED_AGGREGATE_TO_REPORT_TYPE = new Map([...BASE_AGGREGATE_TO_REPORT_TYPE]);

export const UGC_AGGREGATE_TO_REPORT_TYPE = new Map([...BASE_AGGREGATE_TO_REPORT_TYPE]);

export const COMMUNITY_AGGREGATE_TO_REPORT_TYPE = new Map([
  [METRIC_DISPLAY_OPTIONS.TOTAL.value, REPORTS.TOTAL_METRIC.value],
  [METRIC_DISPLAY_OPTIONS.TABLE.value, REPORTS.TOTAL_GROUPED_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.LINE_CHART.value, REPORTS.GRAPH.value],
]);

export const COMPETITIVE_AGGREGATE_TO_REPORT_TYPE = new Map([
  [GRAPH_DISPLAY_OPTIONS.LINE_CHART.value, REPORTS.COMPETITIVE_GRAPH.value],
  [GRAPH_DISPLAY_OPTIONS.SINGLE_BAR_GRAPH.value, REPORTS.COMPETITIVE_BAR_METRIC.value],
]);

export const ADS_AGGREGATE_TO_REPORT_TYPE = new Map([
  [METRIC_DISPLAY_OPTIONS.TOTAL.value, REPORTS.ADS_TOTAL_METRIC.value],
  [METRIC_DISPLAY_OPTIONS.TABLE.value, REPORTS.ADS_TOTAL_GROUPED_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.SINGLE_BAR_GRAPH.value, REPORTS.ADS_BAR_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.STACKED_BAR_GRAPH.value, REPORTS.ADS_STACKED_BAR_METRIC.value],
]);

export const CREATOR_AGGREGATE_TO_REPORT_TYPE = new Map([
  [METRIC_DISPLAY_OPTIONS.TOTAL.value, REPORTS.CREATOR_TOTAL_METRIC.value],
  [METRIC_DISPLAY_OPTIONS.TABLE.value, REPORTS.CREATOR_TOTAL_GROUPED_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.LINE_CHART.value, REPORTS.CREATOR_LINE_GRAPH.value],
  [GRAPH_DISPLAY_OPTIONS.SINGLE_BAR_GRAPH.value, REPORTS.CREATOR_BAR_METRIC.value],
]);

export const EARNED_TO_REPORT_TYPE = {
  CREATORS: CREATOR_AGGREGATE_TO_REPORT_TYPE,
  UGC: UGC_AGGREGATE_TO_REPORT_TYPE,
};

export const TSI_AGGREGATE_TO_REPORT_TYPE = new Map([
  [METRIC_DISPLAY_OPTIONS.TOTAL.value, REPORTS.TOTAL_METRIC.value],
  [METRIC_DISPLAY_OPTIONS.TABLE.value, REPORTS.TOTAL_GROUPED_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.LINE_CHART.value, REPORTS.GRAPH.value],
  [GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value, REPORTS.BAR_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS_FOR_MENU.DOUGHNUT_CHART.value, REPORTS.DOUGHNUT_METRIC.value],
]);

export const SOCIAL_LISTENING_AGGREGATE_TO_REPORT_TYPE = new Map([
  [METRIC_DISPLAY_OPTIONS.TOTAL.value, REPORTS.TOTAL_METRIC.value],
  [METRIC_DISPLAY_OPTIONS.TABLE.value, REPORTS.TOTAL_GROUPED_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.LINE_CHART.value, REPORTS.GRAPH.value],
  [GRAPH_DISPLAY_OPTIONS.SINGLE_BAR_GRAPH.value, REPORTS.BAR_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS.STACKED_BAR_GRAPH.value, REPORTS.STACKED_BAR_METRIC.value],
  [GRAPH_DISPLAY_OPTIONS_FOR_MENU.HORIZONTAL_BAR_CHART.value, REPORTS.HORIZONTAL_BAR_METRIC.value],
]);

// SET new report category to report type mapping
// needed for mapping the report type before saving the report form
// also need for updating the report preview, if you don't set the proper report type,
// the preview will not show the correct visualization
export const REPORT_CATEGORY_TO_REPORT_TYPE = new Map([
  [REPORT_CATEGORY_OPTIONS.OWNED.id, OWNED_AGGREGATE_TO_REPORT_TYPE],
  [REPORT_CATEGORY_OPTIONS.COMPETITIVE.id, COMPETITIVE_AGGREGATE_TO_REPORT_TYPE],
  [REPORT_CATEGORY_OPTIONS.SOCIAL_ADVERTISING.id, ADS_AGGREGATE_TO_REPORT_TYPE],
  [REPORT_CATEGORY_OPTIONS.EARNED.id, EARNED_TO_REPORT_TYPE],
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id, COMMUNITY_AGGREGATE_TO_REPORT_TYPE],
  [REPORT_CATEGORY_OPTIONS.TSI.id, TSI_AGGREGATE_TO_REPORT_TYPE],
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id, SOCIAL_LISTENING_AGGREGATE_TO_REPORT_TYPE],
]);

export const DISPLAY_STRATEGY_OPTIONS = Object.freeze({
  DEFAULT_VALUE: {
    value: 'DEFAULT_VALUE',
    label: 'Display as Total',
    inputLabel: 'Display as Total',
    suffix: 'as Total',
    tooltip: 'The metric value shown for each row (e.g., total messages received by a channel)',
  },
  PERCENT_OF_GRAND_TOTAL: {
    value: 'PERCENT_OF_GRAND_TOTAL',
    label: 'Display as % of Total',
    inputLabel: 'Display as % of Total',
    suffix: 'as % of Total',
    topicLabel: 'Share of Voice by',
    tooltip:
      "The percentage each row contributes to the table total (e.g., a channel's share of total messages received)",
  },
});

// MENU VISUALIZATION AND BREAKDOWN OPTIONS

export const COMMUNITY_TABLE_BREAKDOWN_OPTIONS = Object.freeze([
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.BRAND,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.SENTIMENT,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.MESSAGE_TYPE,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.MESSAGE_TAG,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.ASSIGNEE,
]);

export const COMMUNITY_GRAPH_BREAKDOWN_OPTIONS = Object.freeze([
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.BRAND,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.SENTIMENT,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.MESSAGE_TYPE,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.ASSIGNEE,
]);

export const TSI_TABLE_BREAKDOWN_OPTIONS = Object.freeze([
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.BRAND,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
]);

export const TSI_GRAPH_BREAKDOWN_OPTIONS = Object.freeze([
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.BRAND,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
]);

export const LISTENING_TABLE_BREAKDOWN_OPTIONS = Object.freeze([
  LISTENING_REPORT_AGGREGATE_BY_TYPES.AGE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CITY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.GENDER,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.INDUSTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MEDIA_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MENTION_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.STATE_PROVINCE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.TOPIC,
]);

export const LISTENING_GRAPH_BREAKDOWN_OPTIONS = Object.freeze([
  LISTENING_REPORT_AGGREGATE_BY_TYPES.AGE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CITY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.GENDER,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.INDUSTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MEDIA_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MENTION_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.STATE_PROVINCE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.TOPIC,
]);

// Visualization options for the report visualization selector

export const BASE_VISUALIZATION_OPTIONS = Object.freeze({
  TOTAL: {
    items: null,
  },
  LINE_CHART: {
    items: null,
  },
});

export const BASE_COMMUNITY_VISUALIZATION_OPTIONS = Object.freeze({
  ...BASE_VISUALIZATION_OPTIONS,
  TABLE: {
    items: COMMUNITY_TABLE_BREAKDOWN_OPTIONS,
  },
  VERTICAL_BAR_CHART: {
    items: COMMUNITY_GRAPH_BREAKDOWN_OPTIONS,
  },
});

export const NESTED_COMMUNITY_VISUALIZATION_OPTIONS_BY_METRICS = Object.freeze({
  MESSAGES_RECEIVED: {
    ...BASE_COMMUNITY_VISUALIZATION_OPTIONS,
  },
  RESPONSE_TIME: {
    ...BASE_COMMUNITY_VISUALIZATION_OPTIONS,
  },
  REPLY_RATE: {
    ...BASE_COMMUNITY_VISUALIZATION_OPTIONS,
  },
});

export const NESTED_TSI_VISUALIZATION_OPTIONS_BY_METRICS = Object.freeze({
  TOTAL_TSI: {
    ...BASE_VISUALIZATION_OPTIONS,
    TABLE: {
      items: TSI_TABLE_BREAKDOWN_OPTIONS,
    },
    VERTICAL_BAR_CHART: {
      items: TSI_GRAPH_BREAKDOWN_OPTIONS,
    },
    DOUGHNUT_CHART: {
      items: TSI_GRAPH_BREAKDOWN_OPTIONS,
    },
  },
});

export const LISTENING_LINE_GRAPH_BREAKDOWN_OPTIONS = Object.freeze([
  LISTENING_REPORT_AGGREGATE_BY_TYPES.TOTAL,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CITY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.INDUSTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MEDIA_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MENTION_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.STATE_PROVINCE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.TOPIC,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.SENTIMENT,
]);

export const LISTENING_NET_SENTIMENT_LINE_GRAPH_BREAKDOWN_OPTIONS = Object.freeze([
  LISTENING_REPORT_AGGREGATE_BY_TYPES.TOTAL,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CITY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.INDUSTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MEDIA_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MENTION_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.STATE_PROVINCE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.TOPIC,
]);

export const BASE_LISTENING_VISUALIZATION_OPTIONS = Object.freeze({
  ...BASE_VISUALIZATION_OPTIONS,
  TABLE: {
    items: LISTENING_TABLE_BREAKDOWN_OPTIONS,
  },
  VERTICAL_BAR_CHART: {
    items: LISTENING_GRAPH_BREAKDOWN_OPTIONS,
  },
  HORIZONTAL_BAR_CHART: {
    items: LISTENING_GRAPH_BREAKDOWN_OPTIONS,
  },
  GRAPH: {
    items: LISTENING_LINE_GRAPH_BREAKDOWN_OPTIONS,
  },
});

export const NET_SENTIMENT_BREAKDOWN_OPTIONS = Object.freeze([
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CHANNEL,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.CITY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.COUNTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.INDUSTRY,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MEDIA_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.MENTION_TYPE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.STATE_PROVINCE,
  LISTENING_REPORT_AGGREGATE_BY_TYPES.TOPIC,
]);

export const NESTED_LISTENING_VISUALIZATION_OPTIONS_BY_METRICS = Object.freeze({
  AVERAGE_ENGAGEMENTS_PER_POST: {
    ...BASE_LISTENING_VISUALIZATION_OPTIONS,
  },
  AVERAGE_IMPRESSIONS_PER_POST: {
    ...BASE_LISTENING_VISUALIZATION_OPTIONS,
  },
  AVG_MENTIONS_TOPIC: {
    ...BASE_LISTENING_VISUALIZATION_OPTIONS,
  },
  NET_SENTIMENT: {
    ...BASE_VISUALIZATION_OPTIONS,
    TABLE: {
      items: NET_SENTIMENT_BREAKDOWN_OPTIONS,
    },
    VERTICAL_BAR_CHART: {
      items: NET_SENTIMENT_BREAKDOWN_OPTIONS,
    },
    HORIZONTAL_BAR_CHART: {
      items: NET_SENTIMENT_BREAKDOWN_OPTIONS,
    },
    GRAPH: {
      items: LISTENING_NET_SENTIMENT_LINE_GRAPH_BREAKDOWN_OPTIONS,
    },
  },
  TOP_KEYWORDS: {
    ...BASE_LISTENING_VISUALIZATION_OPTIONS,
  },
  TOTAL_ENGAGEMENTS_TOPIC: {
    ...BASE_LISTENING_VISUALIZATION_OPTIONS,
  },
  TOTAL_IMPRESSIONS_TOPIC: {
    ...BASE_LISTENING_VISUALIZATION_OPTIONS,
  },
  TOTAL_MENTIONS_TOPIC: {
    ...BASE_LISTENING_VISUALIZATION_OPTIONS,
  },
});

export const NESTED_VISUALIZATION_OPTIONS_BY_CATEGORY = Object.freeze({
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: NESTED_COMMUNITY_VISUALIZATION_OPTIONS_BY_METRICS,
  [REPORT_CATEGORY_OPTIONS.TSI.id]: NESTED_TSI_VISUALIZATION_OPTIONS_BY_METRICS,
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: NESTED_LISTENING_VISUALIZATION_OPTIONS_BY_METRICS,
});

export const COMMUNITY_STACKED_BAR_BREAKDOWN_OPTIONS = Object.freeze([
  BASE_BREAKDOWN_OPTIONS.SENTIMENT.value,
  BASE_BREAKDOWN_OPTIONS.ASSIGNEE.value,
  BASE_BREAKDOWN_OPTIONS.MESSAGE_TYPE.value,
]);

export const TSI_STACKED_BAR_BREAKDOWN_OPTIONS = Object.freeze([
  BASE_BREAKDOWN_OPTIONS.BRAND.value,
  BASE_BREAKDOWN_OPTIONS.CHANNEL.value,
]);

export const LISTENING_STACKED_BAR_BREAKDOWN_OPTIONS = Object.freeze([
  BASE_BREAKDOWN_OPTIONS.AGE.value,
  BASE_BREAKDOWN_OPTIONS.CHANNEL.value,
  BASE_BREAKDOWN_OPTIONS.CITY.value,
  BASE_BREAKDOWN_OPTIONS.COUNTRY.value,
  BASE_BREAKDOWN_OPTIONS.GENDER.value,
  BASE_BREAKDOWN_OPTIONS.INDUSTRY.value,
  BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE.value,
  BASE_BREAKDOWN_OPTIONS.MENTION_TYPE.value,
  BASE_BREAKDOWN_OPTIONS.SENTIMENT.value,
  BASE_BREAKDOWN_OPTIONS.STATE_PROVINCE.value,
  BASE_BREAKDOWN_OPTIONS.TOPIC.value,
]);

export const STACKED_BAR_BREAKDOWN_OPTIONS_BY_CATEGORY = Object.freeze({
  [REPORT_CATEGORY_OPTIONS.COMMUNITY.id]: {
    [GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value]: {
      [BASE_BREAKDOWN_OPTIONS.NONE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.NONE,
      },
      [BASE_BREAKDOWN_OPTIONS.SENTIMENT.value]: {
        ...BASE_BREAKDOWN_OPTIONS.SENTIMENT,
      },
      [BASE_BREAKDOWN_OPTIONS.ASSIGNEE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.ASSIGNEE,
      },
      [BASE_BREAKDOWN_OPTIONS.MESSAGE_TYPE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.MESSAGE_TYPE,
      },
    },
  },
  [REPORT_CATEGORY_OPTIONS.TSI.id]: {
    [GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value]: {
      [BASE_BREAKDOWN_OPTIONS.NONE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.NONE,
      },
      [BASE_BREAKDOWN_OPTIONS.BRAND.value]: {
        ...BASE_BREAKDOWN_OPTIONS.BRAND,
      },
      [BASE_BREAKDOWN_OPTIONS.CHANNEL.value]: {
        ...BASE_BREAKDOWN_OPTIONS.CHANNEL,
      },
    },
  },
  [REPORT_CATEGORY_OPTIONS.SOCIAL_LISTENING.id]: {
    [GRAPH_DISPLAY_OPTIONS_FOR_MENU.VERTICAL_BAR_CHART.value]: {
      [BASE_BREAKDOWN_OPTIONS.NONE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.NONE,
      },
      [BASE_BREAKDOWN_OPTIONS.AGE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.AGE,
        hideByAggregation: [
          BASE_BREAKDOWN_OPTIONS.COUNTRY.value,
          BASE_BREAKDOWN_OPTIONS.SENTIMENT.value,
        ],
      },
      [BASE_BREAKDOWN_OPTIONS.CHANNEL.value]: {
        ...BASE_BREAKDOWN_OPTIONS.CHANNEL,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.CITY.value]: {
        ...BASE_BREAKDOWN_OPTIONS.CITY,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.COUNTRY.value]: {
        ...BASE_BREAKDOWN_OPTIONS.COUNTRY,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.GENDER.value]: {
        ...BASE_BREAKDOWN_OPTIONS.GENDER,
        hideByAggregation: [
          BASE_BREAKDOWN_OPTIONS.COUNTRY.value,
          BASE_BREAKDOWN_OPTIONS.SENTIMENT.value,
        ],
      },
      [BASE_BREAKDOWN_OPTIONS.INDUSTRY.value]: {
        ...BASE_BREAKDOWN_OPTIONS.INDUSTRY,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.MENTION_TYPE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.MENTION_TYPE,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.SENTIMENT.value]: {
        ...BASE_BREAKDOWN_OPTIONS.SENTIMENT,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.SENTIMENT.value],
      },
      [BASE_BREAKDOWN_OPTIONS.STATE_PROVINCE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.STATE_PROVINCE,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.TOPIC.value]: {
        ...BASE_BREAKDOWN_OPTIONS.TOPIC,
      },
    },
    [GRAPH_DISPLAY_OPTIONS_FOR_MENU.HORIZONTAL_BAR_CHART.value]: {
      [BASE_BREAKDOWN_OPTIONS.NONE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.NONE,
      },
      [BASE_BREAKDOWN_OPTIONS.AGE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.AGE,
        hideByAggregation: [
          BASE_BREAKDOWN_OPTIONS.COUNTRY.value,
          BASE_BREAKDOWN_OPTIONS.SENTIMENT.value,
        ],
      },
      [BASE_BREAKDOWN_OPTIONS.CHANNEL.value]: {
        ...BASE_BREAKDOWN_OPTIONS.CHANNEL,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.CITY.value]: {
        ...BASE_BREAKDOWN_OPTIONS.CITY,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.COUNTRY.value]: {
        ...BASE_BREAKDOWN_OPTIONS.COUNTRY,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.GENDER.value]: {
        ...BASE_BREAKDOWN_OPTIONS.GENDER,
        hideByAggregation: [
          BASE_BREAKDOWN_OPTIONS.COUNTRY.value,
          BASE_BREAKDOWN_OPTIONS.SENTIMENT.value,
        ],
      },
      [BASE_BREAKDOWN_OPTIONS.INDUSTRY.value]: {
        ...BASE_BREAKDOWN_OPTIONS.INDUSTRY,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.MEDIA_TYPE,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.MENTION_TYPE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.MENTION_TYPE,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.SENTIMENT.value]: {
        ...BASE_BREAKDOWN_OPTIONS.SENTIMENT,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.SENTIMENT.value],
      },
      [BASE_BREAKDOWN_OPTIONS.STATE_PROVINCE.value]: {
        ...BASE_BREAKDOWN_OPTIONS.STATE_PROVINCE,
        hideByAggregation: [BASE_BREAKDOWN_OPTIONS.COUNTRY.value],
      },
      [BASE_BREAKDOWN_OPTIONS.TOPIC.value]: {
        ...BASE_BREAKDOWN_OPTIONS.TOPIC,
      },
    },
  },
});
